// 2DS Finland Oy — Bilingual translation dictionary (EN/FI)
// Content fully aligned to the 2DS Finland Oy presentation deck.
// Tone: PR-grade, warm, co-founder/anchor — not vendor.

const translations = {
  en: {
    // Navigation
    nav: {
      whyFinland: "Why Finland",
      barriers: "The Reality",
      coFounder: "Why 2DS",
      journey: "Journey",
      services: "Services",
      rightFor: "Right For You",
      recognition: "Trust",
      ventures: "Ventures",
      contact: "Talk",
    },

    // Hero Section — PDF p.1
    hero: {
      badge: "Since 2021",
      headline1: "Your trusted partner",
      headline2: "for entering Finland",
      headline3: "& the Nordics.",
      description: "Expanding to Finland? <strong>We handle everything</strong> — so you can stay focused on building your business while we set up, anchor, and grow your presence here.",
      ctaPrimary: "Book a free 20-min call",
      ctaSecondary: "See how we work",
      statsTitle: "Why founders choose 2DS",
      statsVentures: "Finnish ventures we operate",
      statsFounded: "On Finnish soil since",
      statsYears: "Years in the India–Nordic corridor",
      statsLocation: "Espoo · Helsinki region",
      businessId: "Business ID",
    },

    // Why Finland — Stats + 4 pillar advantages + India corridor traction
    marketDrivers: {
      label: "Why Finland · Why Now",
      headline: "Your gateway to the EU & Nordics.",
      description: "Government-backed funding, a world-class innovation ecosystem, talent, and direct access to 450M+ EU consumers — all anchored in one of the most stable economies in Europe.",
      stats: [
        { value: "€1.2B", label: "Finnish VC funding · 2023" },
        { value: "450M+", label: "EU consumer market · direct access" },
        { value: "#3", label: "Global Innovation Index" },
        { value: "100%", label: "Public services digitalised" }
      ],
      pillarsTitle: "Four advantages, working in your favour",
      pillars: [
        {
          icon: "building",
          title: "Government & institutional support",
          body: "Business Finland funding, Team Finland embassy network, and Invest in Finland walk every step with you."
        },
        {
          icon: "rocket",
          title: "Innovation ecosystem",
          body: "Maria 01 — Europe's largest startup campus, Kiuas accelerator, and an active VC and angel community."
        },
        {
          icon: "compass",
          title: "Strategic market position",
          body: "Eurozone stability, bridge to €1.7T Nordic + Baltic GDP, and seamless single-market reach."
        },
        {
          icon: "users",
          title: "World-class talent",
          body: "Top-ranked education system, 97% English proficiency, and a deep engineering & gaming heritage."
        }
      ],
      india: {
        label: "India ↔ Finland · the momentum is now",
        body: "An India-EU FTA concluded January 2026, President Stubb's March 2026 state visit launching a Strategic Partnership in Digitalisation & Sustainability with a goal to double bilateral trade by 2030, the Joint Innovation Call (€250K × 6 projects in deep-tech, microelectronics & energy), Tech Mahindra's new Espoo Innovation Centre, AM Green's acquisition of Chempolis Oy — the corridor has never been more active.",
        proofs: [
          "India–EU FTA · Jan 2026",
          "State Visit · Mar 2026 · 2030 trade goal",
          "Joint Innovation Call · €1.5M",
          "100+ Finnish firms in India"
        ]
      }
    },

    // Sound Familiar? — Compressed: 4 barriers, one-line solutions, no redundant closing callout
    barriers: {
      label: "Sound Familiar?",
      headlineLead: "You're not alone.",
      headlineEm: "Most founders discover Finland's complexity only after they've committed.",
      description: "Here's what typically stands in the way — and what we solve for you.",
      ourSolution: "How 2DS solves it",
      items: [
        {
          title: "Administrative complexity",
          description: "Registrations, PRH, VAT, banking — the paperwork alone can stall your launch by months.",
          solution: "Full administrative track in-house — sequenced so your launch holds the calendar."
        },
        {
          title: "Language & cultural barriers",
          description: "Official documents in Finnish, unwritten business norms, and a culture that values trust over speed.",
          solution: "We translate the substance and the silence — meetings pre-briefed for a Finnish room."
        },
        {
          title: "Fragmented service landscape",
          description: "Lawyers, accountants, and HR consultants who don't talk to each other? You manage the managers.",
          solution: "One partner. One point of accountability. Every function under one Espoo address."
        },
        {
          title: "High initial costs",
          description: "Professional fees pile up — especially when you're paying for advice that doesn't turn into action.",
          solution: "Retained execution, not hourly memos. You pay for the entity standing and the doors opening."
        }
      ]
    },

    // What You Get With 2DS — Compressed: headline + intro + Consultant vs Co-founder distinction only
    coFounder: {
      label: "What You Get With 2DS",
      headlineLead: "Not a consultant.",
      headlineEm: "A co-founder on the ground.",
      description: "We act as your co-founder in Finland — embedded as your local partner, accountable for outcomes, and invested in your growth. The distinction matters.",
      distinction: {
        label: "The Difference",
        consultant: {
          title: "A consultant",
          points: ["Hands you a report", "Bills the hour", "Disappears at handover", "Owns no outcome"]
        },
        coFounder: {
          title: "A co-founder",
          points: ["Embeds as your local partner", "Holds the relationships", "Manages the risk", "Stays accountable — day one through growth"]
        },
        line: "That's the difference between a consultant and a co-founder."
      }
    },

    // Your Journey With Us — PDF p.4
    journey: {
      label: "Your Journey With Us",
      headlineLead: "Start to scale.",
      headlineEm: "Five phases. One partner. Every step.",
      description: "From your very first question to a fully operational business — we walk every step with you. Here's what that looks like.",
      steps: [
        {
          number: "01",
          title: "Feasibility",
          tag: "Decide with clarity",
          description: "We assess whether Finland is the right move for you — market fit, regulatory landscape, entity structure, and tax routes. You get a clear go/no-go recommendation."
        },
        {
          number: "02",
          title: "Landing",
          tag: "Get legally established",
          description: "We coordinate your company incorporation, PRH registration, registered office, banking, and VAT setup — working with licensed legal and tax advisors on your behalf."
        },
        {
          number: "03",
          title: "Operations",
          tag: "Get the business running",
          description: "Vendor onboarding, local hiring support, contracts, and supplier coordination — so you're operational, not just registered."
        },
        {
          number: "04",
          title: "Administration",
          tag: "Stay compliant & on track",
          description: "Bookkeeping, payroll, VAT filings, and a managed compliance calendar — nothing falls through the cracks."
        },
        {
          number: "05",
          title: "Business Development",
          tag: "Open the right doors",
          description: "Market access, partner introductions, investor connections, and pipeline development across the India–Nordic corridor."
        }
      ]
    },

    // Services — Under One Roof — PDF p.5 (6 categories)
    services: {
      label: "Everything Under One Roof",
      headlineLead: "Everything you need —",
      headlineEm: "one integrated partner.",
      description: "Stop coordinating between lawyers, accountants, and consultants who don't talk to each other. With 2DS, <strong>every critical function lives in one place</strong> — from day one to full operation.",
      cta: "Discuss your engagement",
      ctaNote: "Indicative engagement scope shared after a 20-minute call.",
      pillars: [
        {
          tag: "Service 01",
          title: "Business Formation",
          description: "Get legally established, fast and correctly.",
          deliverables: [
            "Oy · branch · sole proprietor registration · PRH · Y-tunnus",
            "VAT registration · banking setup · shareholder agreements"
          ]
        },
        {
          tag: "Service 02",
          title: "Financial Services",
          subtitle: "Powered by Selkotili",
          description: "Stay compliant without the headache.",
          deliverables: [
            "AI-assisted bookkeeping · VAT automation · payroll",
            "Monthly reporting · audit prep · tax optimisation"
          ]
        },
        {
          tag: "Service 03",
          title: "Human Resources",
          description: "Hire right, from day one.",
          deliverables: [
            "Employment contracts · work/residence permits",
            "Finnish labour-law compliance · relocation support"
          ]
        },
        {
          tag: "Service 04",
          title: "Administrative Support",
          description: "Your local presence, professionally managed.",
          deliverables: [
            "Registered address · mail handling · translation",
            "Meeting facilities · document certification"
          ]
        },
        {
          tag: "Service 05",
          title: "Market Entry Consulting",
          description: "Enter with confidence, not guesswork.",
          deliverables: [
            "Partner & investor matching · go-to-market strategy",
            "Feasibility studies · regulatory & market sizing"
          ]
        },
        {
          tag: "Service 06",
          title: "Grants & Growth Advisory",
          description: "Access funding you didn't know existed.",
          deliverables: [
            "Business Finland · EU (Horizon Europe) grants",
            "VC-ready pitch · accelerator introductions"
          ]
        }
      ]
    },

    // Is 2DS Right for You? — PDF p.6
    rightForYou: {
      label: "Is 2DS Right For You?",
      headlineLead: "Who we work with —",
      headlineEm: "and what makes us different.",
      whoLabel: "Who we work with",
      whoItems: [
        {
          title: "International companies",
          description: "You want a Finnish subsidiary or Nordic HQ but don't have the local knowledge or bandwidth to set it up yourself."
        },
        {
          title: "Global startups",
          description: "Tech founder drawn to Finland's innovation ecosystem, EU market access, and VC funding — and you need a fast, compliant launch."
        },
        {
          title: "Local entrepreneurs",
          description: "Finnish founder who needs operational and administrative support so you can focus entirely on your product."
        },
        {
          title: "Foreign investors",
          description: "You're investing in Finnish assets and need a trusted local representative to manage and protect your interests."
        }
      ],
      comparisonLabel: "Why not the alternatives?",
      comparisonHeader: ["", "What they do", "What they don't do"],
      comparisonRows: [
        { name: "Accounting firms", does: "Books, tax filings, payroll", doesnt: "No BD, no market entry, no operations" },
        { name: "Law firms", does: "Legal documents, advisory", doesnt: "High cost, narrow focus, no delivery" },
        { name: "Government services", does: "Generic guidance, brochures", doesnt: "Slow response, no hands-on execution" },
        { name: "Consultancies", does: "Strategy decks, recommendations", doesnt: "Theoretical advice, no implementation" },
        { name: "2DS Finland Oy", does: "All of the above — integrated", doesnt: "Nothing falls through the cracks", highlight: true }
      ]
    },

    // Recognition — PDF p.7
    recognition: {
      label: "You're In Trusted Hands",
      headline: "Credentials we don't have to claim. They're verified.",
      description: "Our credentials aren't self-declared — they're independently verified by the institutions that matter most in Finland and the India–Nordic corridor.",
      verify: "Verify listing",
      items: [
        {
          name: "Embassy of India, Helsinki",
          role: "Government of India — official trusted-company directory",
          description: "Listed on the official Government of India directory of trusted Indian companies operating in Finland. An Embassy-recognised mandate for India–Finland corridor work.",
          url: "https://www.indembhelsinki.gov.in/indian-companies-in-finland.php",
          badge: "Embassy of India"
        },
        {
          name: "Helsinki Region Chamber of Commerce",
          role: "Kauppakamari · Keskuskauppakamari network",
          description: "Verified member of the Keskuskauppakamari network — Finland's central business representative body, confirming our standing in the Finnish business community.",
          url: "https://www.kauppakamariverkosto.fi/jasenprofiilit/2ds-finland-oy/",
          badge: "Kauppakamari"
        },
        {
          name: "Helsinki Partners",
          role: "Helsinki's official inward investment agency",
          description: "Active cooperation with Helsinki's official inward investment and business development agency, supporting international companies entering the city-region.",
          url: "https://www.myhelsinki.fi/business/featured-business-services-in-helsinki/",
          badge: "Helsinki Partners"
        }
      ],
      tagline: "1 partner for your full journey — from feasibility through to growth."
    },

    // Where We're Headed — Roadmap — PDF p.8
    roadmap: {
      label: "Where We're Headed",
      headlineLead: "Where we're headed —",
      headlineEm: "and how you benefit.",
      description: "We're building the most comprehensive India–Nordic corridor platform — and our clients grow with us at every stage.",
      phases: [
        {
          tag: "Phase 1",
          title: "Foundation",
          items: [
            "Structured onboarding for your first 20+ clients across sectors",
            "Proven service delivery — reliable from day one",
            "5+ strategic referral partnerships already in place",
            "Brand presence in key India and Finland markets"
          ]
        },
        {
          tag: "Phase 2",
          title: "Enhancement",
          items: [
            "Digital client portal — streamlined onboarding and visibility",
            "Expanded funding advisory to help you access grants and VC",
            "Preliminary presence in Sweden and Estonia for your Nordic expansion",
            "Thought leadership content to support your market positioning"
          ]
        },
        {
          tag: "Phase 3",
          title: "Expansion",
          items: [
            "Physical Nordic office for in-person client support",
            "Soft-Landing Accelerator programme for fast-track market entry",
            "Full-service capability across the Nordic region",
            "Specialised verticals for tech and cleantech clients"
          ]
        }
      ],
      edge: {
        title: "Proven demand · unique edge",
        body: "Finland is actively sought by India-corridor and deep-tech companies — but integrated, specialist support is rare. 15+ years of India–Nordic delivery, Embassy of India recognition, and an active deep-tech pipeline mean you're backed by a partner with real, inimitable advantages."
      }
    },

    // Are You the Right Fit? — PDF p.9 (5 detailed profiles)
    audience: {
      label: "Are You The Right Fit?",
      headline: "We work best with organisations that are serious about Finland.",
      description: "If any of the below sounds like you — let's talk.",
      items: [
        {
          title: "Indian Deep-Tech Founders",
          description: "Quantum, semiconductor, cybersecurity, or AI company asking \"Is Finland viable for us?\" We answer that question clearly, then execute the entire entry end-to-end."
        },
        {
          title: "Finnish Corporates & Enterprises",
          description: "Looking for structured, trusted access to India's tech talent, market, or supply chain? We're your corridor partner on both sides."
        },
        {
          title: "Investors & VCs",
          description: "Interested in co-investment opportunities in regulated-sector, high-IP India-origin ventures? We have an active pipeline ready for the right partners."
        },
        {
          title: "Innovation Ecosystems & Accelerators",
          description: "Running a programme like Maria 01, Kiuas, or Business Finland? We bring India-origin companies into Finnish ecosystems — structured, vetted, and ready."
        },
        {
          title: "Public Sector & Government Bodies",
          description: "Working in defence, cybersecurity, or quantum tech? We're a Finnish-registered, Embassy-anchored partner built for sensitive, regulated engagement."
        }
      ]
    },

    // Ventures — RETAINED (user requirement)
    ventures: {
      label: "Our Own Digital Ventures",
      headline: "Proof we operate. Not just advise.",
      description: "We don't recommend a step we haven't already taken. These four Finnish-built products are how we keep our hands on the soil — and how we know what really works here.",
      items: [
        {
          name: "TourPilot.biz",
          tagline: "AI-Powered Tour Operator ERP",
          highlight: "Travel Tech SaaS",
          description: "Professional software for tour operators to manage packages, track bookings, monitor costs, and optimise operations — augmented with AI for itinerary intelligence and pricing.",
          features: ["AI Itinerary Intelligence", "B2B Tour Operators"]
        },
        {
          name: "FinnDMC.com",
          tagline: "Finland's Destination Management Company",
          highlight: "Inbound Travel · Nordic",
          description: "Curated Finnish and Nordic experiences for inbound clients — bespoke itineraries, corporate travel programmes, and MICE (meetings, incentives, conferences, events).",
          features: ["Inbound Programmes", "Corporate & MICE"]
        },
        {
          name: "Selkotili.com",
          tagline: "Reliable, effortless, affordable AI bookkeeping",
          highlight: "AI FinTech · Finland",
          description: "Finnish AI-powered bookkeeping platform. Automated receipt classification, VAT (ALV) calculations, and bank reconciliation — fully compliant with KPL, KPA, EVL and AVL. From €39/mo + VAT.",
          features: ["AI Bookkeeping", "KPL · KPA · EVL · AVL"]
        },
        {
          name: "Vanit.app",
          tagline: "Live · vanit.app",
          highlight: "Consumer Platform",
          description: "An emerging consumer platform we've built from the ground up. Now live and onboarding early users — partnership conversations welcome.",
          features: ["Consumer Category", "Live · 2026"]
        },
        {
          name: "BizValuer.co",
          tagline: "Research-backed business valuation",
          highlight: "Business Finance",
          description: "Free business valuation calculator supporting startups and traditional businesses. Six methodologies including DCF, with geographic adjustments and exit modelling.",
          features: ["6 Valuation Methods", "Free to Use"]
        }
      ],
      ndaTitle: "+ 3 client ventures under NDA",
      ndaBody: "We also operate three additional client ventures we cannot disclose publicly — covered under signed NDAs across deep-tech, regulated sectors and corridor partnerships."
    },

    // Final CTA + Founder — PDF p.10
    finalCta: {
      label: "Ready to Enter Finland?",
      headlineLead: "Let's talk.",
      headlineEm: "Twenty minutes. No obligation. Just clarity.",
      description: "Whether you're an Indian deep-tech founder, a Finnish corporate exploring the India corridor, an investor, or an ecosystem builder — your next step starts with a short conversation.",
      promise: {
        label: "Our promise to you",
        headline: "A real partner. Not a vendor.",
        body: "When you write to us, a member of our founding team reads it personally and replies fast. No call-centre. No filtering. No template responses.",
        commitment: "We read every form personally — and we respond within one business day."
      },
      channels: {
        emailLabel: "Email",
        whatsappLabel: "WhatsApp",
        whatsappCta: "Message us",
        webLabel: "Web",
        locationLabel: "Location",
        location: "Espoo, Finland · Y-tunnus 3246359-5"
      },
      formLabel: "Send us a note",
      formNote: "We typically reply within one business day."
    },

    // Contact Form labels (used by ContactForm.jsx)
    contactForm: {
      label: "Send a note",
      headlineLead: "Four fields.",
      headlineEm: "A founder reads every one.",
      description: "Skip the calendar dance — send one note and a member of our founding team replies personally within one business day."
    },

    // Contact aside
    contact: {
      venturesLabel: "Explore our ventures"
    },

    // Footer
    footer: {
      description: "2DS Finland Oy — your trusted partner for entering Finland & the Nordics. Embassy of India listed · Helsinki Region Chamber of Commerce member · Helsinki Partners cooperation.",
      venturesLabel: "Ventures",
      companyLabel: "Company",
      privacyPolicy: "Privacy Policy",
      cookiePolicy: "Cookie Policy",
      operationPolicy: "Operation Policy",
      cookieSettings: "Cookie Settings",
      copyright: "All rights reserved.",
      tagline: "Your India–Nordic corridor partner from day one."
    }
  },

  fi: {
    // Navigation
    nav: {
      whyFinland: "Miksi Suomi",
      barriers: "Todellisuus",
      coFounder: "Miksi 2DS",
      journey: "Matka",
      services: "Palvelut",
      rightFor: "Sopiiko sinulle?",
      recognition: "Luottamus",
      ventures: "Tuotteet",
      contact: "Ota yhteyttä",
    },

    // Hero
    hero: {
      badge: "Vuodesta 2021",
      headline1: "Luotettava kumppanisi",
      headline2: "Suomeen ja",
      headline3: "Pohjoismaihin.",
      description: "Laajentaako liiketoimintaasi Suomeen? <strong>Hoidamme kaiken</strong> — voit keskittyä liiketoimintasi rakentamiseen, kun me perustamme, ankkuroimme ja kasvatamme läsnäoloasi täällä.",
      ctaPrimary: "Varaa ilmainen 20 min puhelu",
      ctaSecondary: "Katso miten toimimme",
      statsTitle: "Miksi perustajat valitsevat 2DS:n",
      statsVentures: "Operoimaamme suomalaista yritystä",
      statsFounded: "Suomen maaperällä vuodesta",
      statsYears: "Vuotta Intia–Pohjola-käytävää",
      statsLocation: "Espoo · pääkaupunkiseutu",
      businessId: "Y-tunnus",
    },

    marketDrivers: {
      label: "Miksi Suomi · Miksi nyt",
      headline: "Porttisi EU:hun ja Pohjoismaihin.",
      description: "Valtion tukema rahoitus, maailmanluokan innovaatioekosysteemi, osaaminen ja suora pääsy yli 450 miljoonan EU-kuluttajan markkinoille — kaikki ankkuroituna yhteen Euroopan vakaimmista talouksista.",
      stats: [
        { value: "1,2 mrd €", label: "Suomalainen VC-rahoitus · 2023" },
        { value: "450M+", label: "EU-kuluttajamarkkinat · suora pääsy" },
        { value: "#3", label: "Globaali innovaatioindeksi" },
        { value: "100%", label: "Julkiset palvelut digitalisoitu" }
      ],
      pillarsTitle: "Neljä etua sinun hyväksesi",
      pillars: [
        {
          icon: "building",
          title: "Valtion ja instituutioiden tuki",
          body: "Business Finlandin rahoitus, Team Finland -lähetystöverkosto ja Invest in Finland kävelevät jokaisen askeleen kanssasi."
        },
        {
          icon: "rocket",
          title: "Innovaatioekosysteemi",
          body: "Maria 01 — Euroopan suurin startup-kampus, Kiuas-kiihdyttämö ja aktiivinen VC- ja enkelisijoittajayhteisö."
        },
        {
          icon: "compass",
          title: "Strateginen markkina-asema",
          body: "Euroalueen vakaus, silta 1,7 biljoonan euron Pohjoismaiden + Baltian BKT:hen ja saumaton sisämarkkinapääsy."
        },
        {
          icon: "users",
          title: "Maailmanluokan osaaminen",
          body: "Maailman parhaiten arvostettu koulutusjärjestelmä, 97 % englannin osaaminen ja syvä insinööri- ja peliteollisuusperintö."
        }
      ],
      india: {
        label: "Intia ↔ Suomi · vauhti on nyt",
        body: "Intian ja EU:n vapaakauppasopimus solmittiin tammikuussa 2026, presidentti Stubbin maaliskuun 2026 valtiovierailu käynnisti Strategisen kumppanuuden digitalisaatiossa ja kestävyydessä tavoitteena kahdentaa kahdenvälinen kauppa vuoteen 2030 mennessä, Joint Innovation Call (250 000 € × 6 hanketta syvätechissä, mikroelektroniikassa ja energiassa), Tech Mahindran uusi Espoon innovaatiokeskus, AM Greenin Chempolis Oy:n hankinta — käytävä ei ole koskaan ollut aktiivisempi.",
        proofs: [
          "Intia–EU FTA · tammikuu 2026",
          "Valtiovierailu · maaliskuu 2026 · 2030 kauppatavoite",
          "Joint Innovation Call · 1,5 M€",
          "100+ suomalaista yritystä Intiassa"
        ]
      }
    },

    barriers: {
      label: "Kuulostaako tutulta?",
      headlineLead: "Et ole yksin.",
      headlineEm: "Useimmat perustajat huomaavat Suomen monimutkaisuuden vasta kun ovat jo sitoutuneet.",
      description: "Nämä esteet tyypillisesti pysäyttävät — ja me ratkaisemme ne puolestasi.",
      ourSolution: "Miten 2DS ratkaisee tämän",
      items: [
        {
          title: "Hallinnollinen monimutkaisuus",
          description: "Rekisteröinnit, PRH, ALV, pankki — pelkkä paperityö voi viivyttää lanseerausta kuukausilla.",
          solution: "Koko hallinnollinen putki sisäisesti — aikataulu pitää."
        },
        {
          title: "Kieli- ja kulttuurimuurit",
          description: "Viralliset asiakirjat suomeksi, kirjoittamattomat normit ja luottamusta arvostava kulttuuri.",
          solution: "Käännämme sisällön ja hiljaisuuden — tapaamiset taustoitetaan suomalaista huonetta varten."
        },
        {
          title: "Pirstaloitunut palvelukenttä",
          description: "Asianajajat, kirjanpitäjät ja HR-konsultit, jotka eivät puhu keskenään? Sinä johdat johtajia.",
          solution: "Yksi kumppani. Yksi vastuupiste. Kaikki saman espoolaisen osoitteen alla."
        },
        {
          title: "Korkeat alkukustannukset",
          description: "Palkkiot kertyvät nopeasti — etenkin kun maksat neuvoista, jotka eivät käänny toiminnaksi.",
          solution: "Retainer-pohjainen toteutus, ei tuntilaskutus. Maksat siitä, että ovet aukeavat."
        }
      ]
    },

    coFounder: {
      label: "Mitä saat 2DS:ltä",
      headlineLead: "Ei konsultti.",
      headlineEm: "Rinnakkaisperustaja paikan päällä.",
      description: "Toimimme rinnakkaisperustajanasi Suomessa — paikallisena kumppanina, tilivelvollisena tuloksista ja sijoittuneena kasvuusi. Ero merkitsee.",
      distinction: {
        label: "Ero",
        consultant: {
          title: "Konsultti",
          points: ["Antaa raportin", "Laskuttaa tunnit", "Katoaa luovutuksessa", "Ei omista lopputulosta"]
        },
        coFounder: {
          title: "Rinnakkaisperustaja",
          points: ["Asettuu paikalliseksi kumppaniksi", "Pitää suhteet hallussa", "Hallitsee riskin", "Pysyy tilivelvollisena — alusta kasvuun"]
        },
        line: "Tämä on ero konsultin ja rinnakkaisperustajan välillä."
      }
    },

    journey: {
      label: "Matkasi kanssamme",
      headlineLead: "Alusta skaalaukseen.",
      headlineEm: "Viisi vaihetta. Yksi kumppani. Jokainen askel.",
      description: "Ensimmäisestä kysymyksestäsi täysin toimivaan liiketoimintaan — kävelemme jokaisen askeleen kanssasi.",
      steps: [
        {
          number: "01",
          title: "Toteutettavuus",
          tag: "Päätös selkeästi",
          description: "Arvioimme, onko Suomi oikea valinta sinulle — markkinasovitus, sääntely-ympäristö, yhtiömuoto ja verotusreitit. Saat selkeän go/no-go-suosituksen."
        },
        {
          number: "02",
          title: "Laskeutuminen",
          tag: "Laillisesti perustettu",
          description: "Koordinoimme yhtiön perustamisen, PRH-rekisteröinnin, rekisteröidyn osoitteen, pankkiyhteyden ja ALV-rekisteröinnin — yhteistyössä lisensoitujen oikeudellisten ja verotuksen asiantuntijoiden kanssa."
        },
        {
          number: "03",
          title: "Operaatiot",
          tag: "Liiketoiminta käyntiin",
          description: "Toimittajaohjelmointi, paikallinen rekrytointituki, sopimukset ja toimittajakoordinointi — toimit, et vain ole rekisteröity."
        },
        {
          number: "04",
          title: "Hallinto",
          tag: "Compliance ja seuranta",
          description: "Kirjanpito, palkanlaskenta, ALV-ilmoitukset ja hallinnoitu compliance-kalenteri — mikään ei putoa läpi."
        },
        {
          number: "05",
          title: "Liiketoiminnan kehitys",
          tag: "Oikeat ovet auki",
          description: "Markkinapääsy, kumppaniesittelyt, sijoittajayhteydet ja putken kehittäminen Intia–Pohjola-käytävällä."
        }
      ]
    },

    services: {
      label: "Kaikki saman katon alla",
      headlineLead: "Kaikki mitä tarvitset —",
      headlineEm: "yksi integroitu kumppani.",
      description: "Lopeta koordinointi asianajajien, kirjanpitäjien ja konsulttien välillä, jotka eivät puhu keskenään. 2DS:n kanssa <strong>jokainen kriittinen toiminto on yhdessä paikassa</strong> — ensimmäisestä päivästä täysoperatiiviseen tilaan.",
      cta: "Keskustele toimeksiannostasi",
      ctaNote: "Suuntaa antava laajuus jaetaan 20 minuutin puhelun jälkeen.",
      pillars: [
        {
          tag: "Palvelu 01",
          title: "Yhtiön perustaminen",
          description: "Laillisesti perustettu, nopeasti ja oikein.",
          deliverables: [
            "Oy · sivuliike · toiminimi -rekisteröinti · PRH · Y-tunnus",
            "ALV-rekisteröinti · pankkiyhteys · osakassopimukset"
          ]
        },
        {
          tag: "Palvelu 02",
          title: "Talouspalvelut",
          subtitle: "Selkotilin voimin",
          description: "Pysy compliance-tasolla ilman päänsärkyä.",
          deliverables: [
            "AI-pohjainen kirjanpito · ALV-automaatio · palkanlaskenta",
            "Kuukausiraportointi · tilintarkastusvalmistelu · veroptimointi"
          ]
        },
        {
          tag: "Palvelu 03",
          title: "Henkilöstöhallinto",
          description: "Rekrytoi oikein, ensimmäisestä päivästä.",
          deliverables: [
            "Työsopimukset · työ- ja oleskeluluvat",
            "Työlainsäädännön compliance · muutto- ja integraatiotuki"
          ]
        },
        {
          tag: "Palvelu 04",
          title: "Hallinnollinen tuki",
          description: "Paikallinen läsnäolosi, ammattimaisesti hoidettuna.",
          deliverables: [
            "Rekisteröity osoite · postin käsittely · kääntäminen",
            "Kokoustilat · asiakirjojen todentaminen"
          ]
        },
        {
          tag: "Palvelu 05",
          title: "Markkinatulokonsultointi",
          description: "Astu varmasti — ei arvailua.",
          deliverables: [
            "Kumppani- ja sijoittajakohtaaminen · go-to-market-strategia",
            "Toteutettavuusanalyysit · sääntely ja markkinakoko"
          ]
        },
        {
          tag: "Palvelu 06",
          title: "Avustukset ja kasvuneuvonta",
          description: "Pääsy rahoitukseen, jota et tiennyt olevan.",
          deliverables: [
            "Business Finland · EU (Horizon Europe) -avustukset",
            "VC-valmis pitch · kiihdyttämöyhteydet"
          ]
        }
      ]
    },

    rightForYou: {
      label: "Sopiiko 2DS sinulle?",
      headlineLead: "Keiden kanssa työskentelemme —",
      headlineEm: "ja mikä tekee meistä erilaisen.",
      whoLabel: "Keiden kanssa työskentelemme",
      whoItems: [
        {
          title: "Kansainväliset yritykset",
          description: "Haluat suomalaisen tytäryhtiön tai Pohjoismaiden pääkonttorin, mutta sinulla ei ole paikallista tietoa tai kapasiteettia rakentaa sitä itse."
        },
        {
          title: "Globaalit startupit",
          description: "Teknologiaperustaja, jota vetää Suomen innovaatioekosysteemi, EU-markkinapääsy ja VC-rahoitus — tarvitset nopean ja compliance-valmiin lanseerauksen."
        },
        {
          title: "Paikalliset yrittäjät",
          description: "Suomalainen perustaja, joka tarvitsee operatiivista ja hallinnollista tukea, jotta voit keskittyä täysin tuotteeseesi."
        },
        {
          title: "Ulkomaiset sijoittajat",
          description: "Sijoitat suomalaisiin omaisuuksiin ja tarvitset luotettavan paikallisen edustajan etujesi hallintaan ja suojaamiseen."
        }
      ],
      comparisonLabel: "Miksi ei vaihtoehdot?",
      comparisonHeader: ["", "Mitä he tekevät", "Mitä he eivät tee"],
      comparisonRows: [
        { name: "Tilitoimistot", does: "Kirjanpito, veroilmoitukset, palkat", doesnt: "Ei BD:tä, ei markkinatuloa, ei operaatioita" },
        { name: "Lakitoimistot", does: "Oikeudelliset asiakirjat, neuvonta", doesnt: "Korkea hinta, kapea fokus, ei toteutusta" },
        { name: "Julkiset palvelut", does: "Yleinen ohjeistus, esitteet", doesnt: "Hidas vastaus, ei käytännön toteutusta" },
        { name: "Konsultointiyhtiöt", does: "Strategiat, suositukset", doesnt: "Teoreettista neuvontaa, ei toteutusta" },
        { name: "2DS Finland Oy", does: "Kaikki yllä — integroituna", doesnt: "Mikään ei putoa läpi", highlight: true }
      ]
    },

    recognition: {
      label: "Luotettavissa käsissä",
      headline: "Ansiot, joita meidän ei tarvitse väittää. Ne on todennettu.",
      description: "Ansiomme eivät ole itse julistettuja — ne on riippumattomasti todennettu Suomen ja Intia–Pohjola-käytävän tärkeimpien instituutioiden toimesta.",
      verify: "Tarkista listaus",
      items: [
        {
          name: "Intian suurlähetystö, Helsinki",
          role: "Intian valtio — virallinen luotettujen yritysten hakemisto",
          description: "Listattuna Intian valtion virallisessa hakemistossa luotettavista intialaisista yrityksistä Suomessa. Suurlähetystön tunnustama mandaatti Intia–Suomi-käytävälle.",
          url: "https://www.indembhelsinki.gov.in/indian-companies-in-finland.php",
          badge: "Intian suurlähetystö"
        },
        {
          name: "Helsingin seudun kauppakamari",
          role: "Kauppakamari · Keskuskauppakamariverkosto",
          description: "Vahvistettu Keskuskauppakamariverkoston jäsen — Suomen keskeinen elinkeinoelämän edustuselin, joka vahvistaa asemamme suomalaisessa liiketoimintayhteisössä.",
          url: "https://www.kauppakamariverkosto.fi/jasenprofiilit/2ds-finland-oy/",
          badge: "Kauppakamari"
        },
        {
          name: "Helsinki Partners",
          role: "Helsingin virallinen invest-in -toimisto",
          description: "Aktiivinen yhteistyö Helsingin virallisen sisäänsijoitus- ja yrityskehitysyksikön kanssa, joka tukee kansainvälisten yritysten tuloa pääkaupunkialueelle.",
          url: "https://www.myhelsinki.fi/business/featured-business-services-in-helsinki/",
          badge: "Helsinki Partners"
        }
      ],
      tagline: "1 kumppani koko matkaasi — toteutettavuudesta kasvuun."
    },

    roadmap: {
      label: "Minne olemme menossa",
      headlineLead: "Minne olemme menossa —",
      headlineEm: "ja miten sinä hyödyt.",
      description: "Rakennamme kattavinta Intia–Pohjola-käytävän alustaa — ja asiakkaamme kasvavat kanssamme jokaisessa vaiheessa.",
      phases: [
        {
          tag: "Vaihe 1",
          title: "Perusta",
          items: [
            "Jäsennelty perehdytys ensimmäiselle 20+ asiakkaalle eri toimialoilta",
            "Todistettu palvelutuotanto — luotettava ensimmäisestä päivästä",
            "5+ strategista suosittelukumppanuutta jo paikallaan",
            "Brändinäkyvyys keskeisillä Intian ja Suomen markkinoilla"
          ]
        },
        {
          tag: "Vaihe 2",
          title: "Tehostus",
          items: [
            "Digitaalinen asiakasportaali — virtaviivainen perehdytys ja näkyvyys",
            "Laajennettu rahoitusneuvonta avustuksiin ja VC-rahaan pääsemiseksi",
            "Alustava läsnäolo Ruotsissa ja Virossa Pohjoismaa-laajentumiseen",
            "Thought leadership -sisältö markkina-asemointisi tueksi"
          ]
        },
        {
          tag: "Vaihe 3",
          title: "Laajennus",
          items: [
            "Fyysinen Pohjoismaa-toimisto kasvokkain tapahtuvaan asiakastukeen",
            "Soft-Landing -kiihdyttämöohjelma nopeaan markkinatuloon",
            "Täyspalvelukapasiteetti koko Pohjoismaiden alueella",
            "Erikoistuneet vertikaalit teknologia- ja cleantech-asiakkaille"
          ]
        }
      ],
      edge: {
        title: "Todistettu kysyntä · ainutlaatuinen etu",
        body: "Intia-käytävän ja syvän teknologian yritykset etsivät aktiivisesti Suomea — mutta integroitu, erikoistunut tuki on harvinaista. 15+ vuotta Intia–Pohjola-toteutusta, Intian suurlähetystön tunnustus ja aktiivinen syvän teknologian putki tarkoittavat, että takanasi on kumppani aidoilla, jäljittelemättömillä eduilla."
      }
    },

    audience: {
      label: "Oletko oikea kumppani?",
      headline: "Toimimme parhaiten organisaatioiden kanssa, jotka ovat tosissaan Suomesta.",
      description: "Jos jokin alla kuulostaa sinulta — ollaan yhteydessä.",
      items: [
        {
          title: "Intialaiset syvän teknologian perustajat",
          description: "Kvantti-, puolijohde-, kyberturva- tai tekoälyfirma, joka kysyy: \"Onko Suomi meille toteuttamiskelpoinen?\" Vastaamme selkeästi ja toteutamme sitten koko sisääntulon päästä päähän."
        },
        {
          title: "Suomalaiset suuryritykset ja konsernit",
          description: "Etsittekö jäsenneltyä, luotettavaa pääsyä Intian teknologiakykyihin, markkinaan tai toimitusketjuun? Olemme käytäväkumppanisi molemmin puolin."
        },
        {
          title: "Sijoittajat ja VC:t",
          description: "Kiinnostunut yhteissijoituksista säänneltyihin, korkean IP:n Intia-taustaisiin yrityksiin? Meillä on aktiivinen putki oikeita kumppaneita varten."
        },
        {
          title: "Innovaatioekosysteemit ja kiihdyttämöt",
          description: "Pyörität ohjelmaa kuten Maria 01, Kiuas tai Business Finland? Tuomme intialaistaustaisia yrityksiä suomalaisiin ekosysteemeihin — jäsenneltyinä, todennettuina ja valmiina."
        },
        {
          title: "Julkinen sektori ja viranomaiset",
          description: "Toimitko puolustus-, kyberturva- tai kvanttitekniikan parissa? Olemme suomalainen, suurlähetystön ankkuroima kumppani, joka on rakennettu herkkään, säänneltyyn sitoutumiseen."
        }
      ]
    },

    ventures: {
      label: "Omat digitaaliset tuotteemme",
      headline: "Todiste siitä, että toimimme. Emme vain neuvo.",
      description: "Emme suosittele askelta, jota emme ole jo itse ottaneet. Nämä neljä Suomessa rakennettua tuotetta pitävät kätemme maaperässä — ja niiden kautta tiedämme, mikä täällä todella toimii.",
      items: [
        {
          name: "TourPilot.biz",
          tagline: "AI-pohjainen matkanjärjestäjän ERP",
          highlight: "Matkailuteknologia SaaS",
          description: "Ammattimainen ohjelmisto matkanjärjestäjille pakettien hallintaan, varausten seurantaan, kustannusten valvontaan ja toiminnan optimointiin – tehostettuna tekoälyllä reittiälyssä ja hinnoittelussa.",
          features: ["AI-reittiäly", "B2B-operaattorit"]
        },
        {
          name: "FinnDMC.com",
          tagline: "Suomen Destination Management -yhtiö",
          highlight: "Inbound-matkailu · Pohjola",
          description: "Räätälöityjä Suomen ja Pohjoismaiden elämyksiä inbound-asiakkaille — kustomoidut reitit, yritysmatkailu ja MICE (kokoukset, kannustematkat, konferenssit, tapahtumat).",
          features: ["Inbound-ohjelmat", "Yritystapahtumat & MICE"]
        },
        {
          name: "Selkotili.com",
          tagline: "Luotettava, vaivaton ja edullinen AI-kirjanpito",
          highlight: "AI FinTech · Suomi",
          description: "Suomalainen tekoälypohjainen kirjanpitoalusta. Automaattinen tositeluokittelu, ALV-laskelmat ja tiliotteiden täsmäytys – täysin yhteensopiva KPL-, KPA-, EVL- ja AVL-säännösten kanssa. Alkaen 39 €/kk + ALV.",
          features: ["AI-kirjanpito", "KPL · KPA · EVL · AVL"]
        },
        {
          name: "Vanit.app",
          tagline: "Käytössä · vanit.app",
          highlight: "Kuluttaja-alusta",
          description: "Itse alusta asti rakennettu kuluttaja-alusta. Nyt käytössä ja ottaa varhaisia käyttäjiä — kumppanuuskeskustelut tervetulleita.",
          features: ["Kuluttajakategoria", "Käytössä · 2026"]
        },
        {
          name: "BizValuer.co",
          tagline: "Tutkimuspohjainen yritysarvonmääritys",
          highlight: "Yritysrahoitus",
          description: "Ilmainen yritysarvonmäärityslaskuri startup- ja perinteisille yrityksille. Kuusi menetelmää mukaan lukien DCF, maantieteelliset korjaukset ja exit-mallinnus.",
          features: ["6 arvonmääritysmenetelmää", "Ilmainen käyttö"]
        }
      ],
      ndaTitle: "+ 3 asiakastoimeksiantoa NDA:n alla",
      ndaBody: "Hoidamme lisäksi kolmea asiakastoimeksiantoa, joita emme voi julkistaa — allekirjoitettujen NDA-sopimusten alla syvän teknologian, säänneltyjen sektorien ja käytäväkumppanuuksien parissa."
    },

    finalCta: {
      label: "Valmiina Suomeen?",
      headlineLead: "Ollaan yhteydessä.",
      headlineEm: "Kaksikymmentä minuuttia. Ilman sitoutumista. Pelkkää selkeyttä.",
      description: "Olitpa intialainen syvän teknologian perustaja, suomalainen konserni Intia-käytävässä, sijoittaja tai ekosysteemin rakentaja — seuraava askeleesi alkaa lyhyellä keskustelulla.",
      promise: {
        label: "Lupauksemme sinulle",
        headline: "Aito kumppani. Ei toimittaja.",
        body: "Kun otat meihin yhteyttä, perustajatiimimme jäsen lukee viestisi henkilökohtaisesti ja vastaa nopeasti. Ei puhelinpalvelua. Ei suodatusta. Ei vakioviestejä.",
        commitment: "Luemme jokaisen lomakkeen henkilökohtaisesti — ja vastaamme yhden arkipäivän kuluessa."
      },
      channels: {
        emailLabel: "Sähköposti",
        whatsappLabel: "WhatsApp",
        whatsappCta: "Viesti meille",
        webLabel: "Verkko",
        locationLabel: "Sijainti",
        location: "Espoo, Suomi · Y-tunnus 3246359-5"
      },
      formLabel: "Lähetä viesti",
      formNote: "Vastaamme tyypillisesti yhden arkipäivän kuluessa."
    },

    contactForm: {
      label: "Lähetä viesti",
      headlineLead: "Neljä kenttää.",
      headlineEm: "Perustaja lukee jokaisen.",
      description: "Ohita kalenteripalapeli — lähetä yksi viesti ja perustajatiimimme jäsen vastaa henkilökohtaisesti yhden arkipäivän kuluessa."
    },

    contact: {
      venturesLabel: "Tutustu tuotteisiimme"
    },

    footer: {
      description: "2DS Finland Oy — luotettava kumppanisi Suomeen ja Pohjoismaihin. Intian suurlähetystön listaama · Helsingin seudun kauppakamarin jäsen · Helsinki Partners -yhteistyö.",
      venturesLabel: "Tuotteet",
      companyLabel: "Yritys",
      privacyPolicy: "Tietosuojakäytäntö",
      cookiePolicy: "Evästekäytäntö",
      operationPolicy: "Toimintaperiaatteet",
      cookieSettings: "Evästeasetukset",
      copyright: "Kaikki oikeudet pidätetään.",
      tagline: "Intia–Pohjola-käytäväkumppanisi ensimmäisestä päivästä."
    }
  }
};

export default translations;
